// GathrXR Studio — client service.
//
// Built against docs/studio-contract.md. Writes to studio_jobs only ever go
// through the edge functions (studio-submit / studio-cancel); the client reads
// its own rows (RLS) and follows one job via Realtime + a 5 s polling fallback.
//
// Importing a finished job mirrors how the hand-built rooms were made (the
// Conference Room recipe): one locked environment GLB at scale 0.8, offset to
// undo SimpleEnvironmentModel's auto-centre/ground, plus invisible seat boxes.
//
// Dev only: `?mock=1` (or `?mock=<status>`) on localhost swaps the backend for
// an in-memory simulation (./studioMock) so every state can be designed before
// the worker exists. The check is behind import.meta.env.DEV, so production
// builds drop it entirely.

import { supabase } from '../config/supabase';
import { uploadBytesToStorage } from '../utils/uploadToStorage';
import { generateSkyboxAndWait } from './blockadeApi';

export type StudioKind = 'world' | 'model';
export type StudioStyle = 'stylized-baked' | 'realistic' | 'low-poly' | 'toon';
export type StudioSize = 'small' | 'medium' | 'large';
/** draft/standard build from the gx kit; hq has Astra model everything freehand. */
export type StudioQuality = 'draft' | 'standard' | 'hq';
export type StudioStatus =
  | 'queued' | 'concept' | 'choosing' | 'planning' | 'building' | 'reviewing' | 'exporting'
  | 'done' | 'failed' | 'canceled';
export type StudioLogKind = 'plan' | 'step' | 'render' | 'note' | 'error';

export interface StudioLogEntry {
  t: string;
  kind: StudioLogKind;
  text: string;
  image_url?: string;
}

export interface StudioOptions {
  style?: StudioStyle;
  size?: StudioSize;
  seats?: boolean;
  experience_type?: string;
  reference_image_url?: string;
  quality?: StudioQuality;
}

export interface StudioSeat {
  position: [number, number, number];
  yaw: number;
}

export interface StudioResultMeta {
  bounds?: { min: [number, number, number]; max: [number, number, number] };
  triangles?: number;
  colliders?: number;
  seats?: StudioSeat[];
  spawn?: [number, number, number];
  title?: string;
  summary?: string;
  /** Optional hint from the worker; otherwise the importer reads the prompt. */
  sky?: { mood?: SkyMood; prompt?: string };
}

export interface StudioJob {
  id: string;
  user_id: string;
  kind: StudioKind;
  prompt: string;
  options: StudioOptions;
  status: StudioStatus;
  progress: number;
  stage_message: string | null;
  log: StudioLogEntry[];
  concept_image_url: string | null;
  /** v2: the 4 low-res reference options to pick from (status `choosing`). */
  reference_options: string[];
  /** v2: how many times the user asked for 4 new options (cap MAX_REGENERATIONS). */
  regenerations: number;
  preview_urls: string[];
  result_glb_url: string | null;
  result_meta: StudioResultMeta | null;
  error: string | null;
  cost_usd: number | null;
  /** Studio credits taken at submit / given back on a system failure or timeout. */
  credits_charged?: number | null;
  credits_refunded?: boolean | null;
  time_limit_s?: number | null;
  deadline_at?: string | null;
  runpod_job_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Statuses after which a job never changes again. */
export const TERMINAL: readonly StudioStatus[] = ['done', 'failed', 'canceled'];
export const isTerminal = (s: StudioStatus) => TERMINAL.includes(s);

/** The pipeline in order, as shown on the stage timeline. */
export const STAGES: readonly StudioStatus[] = ['concept', 'planning', 'building', 'reviewing', 'exporting', 'done'];

/** v2: regenerations allowed per job (contract: cap 3). */
export const MAX_REGENERATIONS = 3;

/** Waiting on the worker to paint the 4 options (no options yet, no upload). */
export const isPaintingOptions = (j: StudioJob) =>
  (j.status === 'queued' || j.status === 'concept') && j.reference_options.length === 0 && !j.options?.reference_image_url;

/** Real-world footprints the worker targets per size (contract `options.size`). */
export const SIZE_DIMENSIONS: Record<StudioSize, { w: number; d: number; label: string }> = {
  small: { w: 6, d: 6, label: 'Small' },
  medium: { w: 12, d: 9, label: 'Medium' },
  large: { w: 25, d: 18, label: 'Large' },
};

/** Scale every Studio world is imported at (matches the hand-built rooms). */
export const WORLD_IMPORT_SCALE = 0.8;

const POLL_MS = 5000;

// ---- Mock switch (dev only) ------------------------------------------------

type MockModule = typeof import('./studioMock');
let mockPromise: Promise<MockModule> | null = null;

/** `?mock=1` (or `?mock=<status>`) on localhost in a dev build. */
export function studioMockParam(): string | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') return null;
  return new URLSearchParams(window.location.search).get('mock');
}

export const isStudioMock = () => !!studioMockParam();

function mock(): Promise<MockModule> | null {
  if (!import.meta.env.DEV || !isStudioMock()) return null;
  if (!mockPromise) mockPromise = import('./studioMock');
  return mockPromise;
}

// ---- Helpers ---------------------------------------------------------------

const JOB_COLUMNS =
  'id,user_id,kind,prompt,options,status,progress,stage_message,log,concept_image_url,reference_options,regenerations,preview_urls,' +
  'result_glb_url,result_meta,error,cost_usd,runpod_job_id,created_at,updated_at,credits_charged,credits_refunded,time_limit_s,deadline_at';

function normalize(row: any): StudioJob {
  return {
    ...row,
    options: row?.options || {},
    log: Array.isArray(row?.log) ? row.log : [],
    preview_urls: Array.isArray(row?.preview_urls) ? row.preview_urls : [],
    reference_options: Array.isArray(row?.reference_options) ? row.reference_options : [],
    regenerations: Number(row?.regenerations) || 0,
    progress: Number(row?.progress) || 0,
    cost_usd: row?.cost_usd == null ? null : Number(row.cost_usd),
  } as StudioJob;
}

/** Pull the JSON error body out of a FunctionsHttpError when there is one. */
async function functionError(error: any, fallback: string): Promise<Error> {
  try {
    const body = await error?.context?.json?.();
    const msg = body?.error || body?.message;
    if (msg) return new Error(String(msg));
  } catch { /* not JSON */ }
  return new Error(error?.message || fallback);
}

// ---- Jobs ------------------------------------------------------------------

export async function submit(kind: StudioKind, prompt: string, options: StudioOptions): Promise<string> {
  const m = mock();
  if (m) return (await m).mockSubmit(kind, prompt, options);
  const { data, error } = await supabase.functions.invoke('studio-submit', { body: { kind, prompt, options } });
  if (error) throw await functionError(error, 'Could not start the job.');
  if (!data?.job_id) throw new Error('Studio did not return a job id.');
  return data.job_id as string;
}

export async function cancel(jobId: string): Promise<void> {
  const m = mock();
  if (m) return (await m).mockCancel(jobId);
  const { data, error } = await supabase.functions.invoke('studio-cancel', { body: { job_id: jobId } });
  if (error) throw await functionError(error, 'Could not cancel the job.');
  if (data && data.ok === false) throw new Error(data.error || 'Could not cancel the job.');
}

/** Remove a finished job from the owner's history (soft delete: the row and
 *  its billing stay; files already added to a world are untouched). */
export async function hideJob(jobId: string): Promise<void> {
  const m = mock();
  if (m) return (await m).mockHide(jobId);
  const { data, error } = await supabase.rpc('studio_hide_job', { p_job_id: jobId });
  if (error) throw new Error(error.message || 'Could not delete it.');
  if (data === false) throw new Error('Only finished, failed or canceled generations can be deleted.');
}

/** v2: answer a job waiting in `choosing` — pick option 0–3, use an upload
 *  instead, or ask for 4 new options. Calls edge function `studio-choose`. */
export type StudioChoice = { choice: number } | { upload_url: string } | { regenerate: true };

export async function choose(jobId: string, body: StudioChoice): Promise<void> {
  const m = mock();
  if (m) return (await m).mockChoose(jobId, body);
  const { data, error } = await supabase.functions.invoke('studio-choose', { body: { job_id: jobId, ...body } });
  if (error) throw await functionError(error, 'Could not send your choice.');
  if (data && data.ok === false) throw new Error(data.error || 'Could not send your choice.');
}

export async function getJob(jobId: string): Promise<StudioJob | null> {
  const m = mock();
  if (m) return (await m).mockGetJob(jobId);
  const { data, error } = await supabase.from('studio_jobs').select(JOB_COLUMNS).eq('id', jobId).maybeSingle();
  if (error) throw error;
  return data ? normalize(data) : null;
}

/** The signed-in user's jobs, newest first. Admins can read everyone's rows
 *  under RLS, so filter to the caller explicitly. */
export async function listJobs(limit = 48): Promise<StudioJob[]> {
  const m = mock();
  if (m) return (await m).mockListJobs();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('studio_jobs')
    .select(JOB_COLUMNS)
    .eq('user_id', user.id)
    .is('hidden_at', null)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map(normalize);
}

/**
 * Follow one job: Realtime postgres_changes on its row plus a 5 s poll (the
 * poll covers dropped sockets and a table not yet in the publication). The
 * callback only ever sees a row newer than the last one delivered. Both stop
 * once the job reaches a terminal status. Returns the unsubscribe function.
 */
export function subscribeJob(jobId: string, cb: (job: StudioJob) => void): () => void {
  let closed = false;
  let lastStamp = '';
  let stopMock: (() => void) | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let channel: ReturnType<typeof supabase.channel> | null = null;

  const deliver = (job: StudioJob | null) => {
    if (closed || !job) return;
    // Same updated_at but a different status/progress can happen when the
    // worker writes twice in a millisecond; include both in the stamp.
    const stamp = `${job.updated_at}|${job.status}|${job.progress}|${job.log.length}|${job.preview_urls.length}|${job.reference_options.join(',')}|${job.regenerations}`;
    if (stamp === lastStamp) return;
    lastStamp = stamp;
    cb(job);
    if (isTerminal(job.status)) stop();
  };

  const stop = () => {
    if (timer) { clearInterval(timer); timer = null; }
    if (channel) { supabase.removeChannel(channel); channel = null; }
    if (stopMock) { stopMock(); stopMock = null; }
  };

  const m = mock();
  if (m) {
    m.then((mod) => { if (!closed) stopMock = mod.mockSubscribe(jobId, deliver); });
    return () => { closed = true; stop(); };
  }

  channel = supabase
    .channel(`studio-job:${jobId}`)
    .on(
      'postgres_changes' as any,
      { event: '*', schema: 'public', table: 'studio_jobs', filter: `id=eq.${jobId}` },
      (payload: any) => { if (payload?.new?.id) deliver(normalize(payload.new)); },
    )
    .subscribe();

  const poll = () => { getJob(jobId).then(deliver).catch(() => { /* next tick */ }); };
  poll();
  timer = setInterval(poll, POLL_MS);

  return () => { closed = true; stop(); };
}

// ---- Reference image -------------------------------------------------------

export async function uploadReferenceImage(file: File): Promise<string> {
  const m = mock();
  // In mock mode nothing leaves the browser: a local object URL stands in.
  if (m) return URL.createObjectURL(file);
  const ext = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
  const key = `studio/references/${crypto.randomUUID()}.${ext}`;
  return uploadBytesToStorage(file, key, file.type || 'image/png');
}

// ---- Import: world ---------------------------------------------------------

const rid = () => Math.random().toString(36).slice(2, 7);
const r3 = (v: number) => Math.round(v * 1000) / 1000;

// ---- Sky ------------------------------------------------------------------

export type SkyMood = 'day' | 'sunset' | 'night' | 'overcast';

/** Pre-made Blockade skies (R2), used when a fresh one can't be painted in time. */
export const PRESET_SKY: Record<SkyMood, string> = {
  day: 'https://assets.gathrxr.com/skyboxes/studio-sky-day.jpg',
  sunset: 'https://assets.gathrxr.com/skyboxes/studio-sky-sunset.jpg',
  night: 'https://assets.gathrxr.com/skyboxes/studio-sky-night.jpg',
  overcast: 'https://assets.gathrxr.com/skyboxes/studio-sky-overcast.jpg',
};

/** Sun/ambient per mood. The GLB carries baked light, so these only tint
 *  avatars and anything added later. */
export const SKY_LIGHT: Record<SkyMood, { sunColor: string; sunIntensity: number; ambientIntensity: number }> = {
  day: { sunColor: '#fff1e0', sunIntensity: 0.8, ambientIntensity: 0.8 },
  sunset: { sunColor: '#ffb877', sunIntensity: 0.6, ambientIntensity: 0.7 },
  night: { sunColor: '#9fb4ff', sunIntensity: 0.3, ambientIntensity: 0.55 },
  overcast: { sunColor: '#dfe6ee', sunIntensity: 0.55, ambientIntensity: 0.75 },
};

const MOOD_WORDS: [SkyMood, RegExp][] = [
  ['night', /\b(night|nighttime|midnight|moon(lit)?|stars?|starry|neon|nightclub|club|after dark|galaxy|aurora)\b/i],
  ['sunset', /\b(sunset|sunrise|dusk|dawn|golden hour|evening|twilight|sundown)\b/i],
  ['overcast', /\b(rain(y)?|overcast|cloudy|fog(gy)?|mist(y)?|storm(y)?|grey day|gray day|snow(y)?)\b/i],
];

/** Time of day for a job: the worker's hint if it gave one, else the prompt. */
export function skyMoodFor(job: StudioJob): SkyMood {
  const hint = job.result_meta?.sky?.mood;
  if (hint && hint in PRESET_SKY) return hint;
  const text = `${job.prompt} ${job.result_meta?.summary || ''}`;
  for (const [mood, re] of MOOD_WORDS) if (re.test(text)) return mood;
  return 'day';
}

const MOOD_TEXT: Record<SkyMood, string> = {
  day: 'Bright clear blue daytime sky with soft fluffy white clouds and warm sunlight',
  sunset: 'Golden-hour sunset sky with warm orange and pink clouds and a glowing sun',
  night: 'Clear night sky full of stars with soft wispy clouds and a glowing moon, deep blue',
  overcast: 'Soft overcast sky with layered grey clouds and gentle diffuse light',
};

const STYLE_TEXT: Record<string, string> = {
  'stylized-baked': 'stylized soft 3D render, smooth and clean',
  realistic: 'photorealistic',
  'low-poly': 'low-poly faceted 3D art, simple palette',
  toon: 'cel-shaded cartoon style, bold flat colors',
};

/** Sky only — no ground, hills or skyline: the GLB is the whole place, and
 *  anything on the horizon would sit oddly next to it. */
export const SKY_NEGATIVE = 'ground, land, hills, mountains, terrain, grass, fields, trees, buildings, city, skyline, water, ocean, horizon line, people';

/** Blockade prompt for a world's sky: the mood in the world's style. */
export function skyPromptFor(job: StudioJob, mood = skyMoodFor(job)): string {
  if (job.result_meta?.sky?.prompt) return job.result_meta.sky.prompt.slice(0, 550);
  const style = STYLE_TEXT[job.options?.style || ''] || STYLE_TEXT['stylized-baked'];
  return `${MOOD_TEXT[mood]}. Only sky and clouds in every direction, a full sky dome with clouds continuing below the horizon, no land at all. ${style}.`;
}

/** Paint a matching sky with Blockade and keep a permanent copy on R2.
 *  The job id makes it the job's one free sky (already paid with Studio
 *  credits; skybox_ai_charge checks it server-side). Falls back to the preset
 *  for the mood if that takes too long, fails, the sky was already painted,
 *  or Skybox AI hit its monthly cap. */
async function skyboxFor(job: StudioJob, onProgress?: (msg: string) => void): Promise<{ url: string; mood: SkyMood; filename: string }> {
  const mood = skyMoodFor(job);
  const preset = { url: PRESET_SKY[mood], mood, filename: `Studio ${mood} sky` };
  try {
    onProgress?.('Painting a matching sky…');
    const timeout = new Promise<never>((_, rej) => setTimeout(() => rej(new Error('sky timeout')), 100_000));
    const { fileUrl } = await Promise.race([generateSkyboxAndWait(skyPromptFor(job, mood), undefined, undefined, SKY_NEGATIVE, { studioJobId: job.id }), timeout]);
    onProgress?.('Saving the sky…');
    // Blockade URLs aren't permanent (same reason SkyboxManager copies them).
    const blob = await (await fetch(fileUrl)).blob();
    const url = await uploadBytesToStorage(blob, `skyboxes/studio-${job.id}-${Date.now()}.jpg`, blob.type || 'image/jpeg');
    return { url, mood, filename: `AI: ${mood} sky for ${(job.result_meta?.title || job.prompt).slice(0, 30)}` };
  } catch (e) {
    console.warn('[Studio] sky generation failed, using preset:', e);
    return preset;
  }
}

/** The rows `save_world` gets for a finished world job. Pure, so the mock
 *  can show exactly what would be written. */
export function buildWorldImport(job: StudioJob, sky?: { url: string; mood: SkyMood; filename: string }) {
  const meta = job.result_meta || {};
  if (!job.result_glb_url) throw new Error('This job has no GLB yet.');
  const s = WORLD_IMPORT_SCALE;
  const min = meta.bounds?.min || [0, 0, 0];
  const max = meta.bounds?.max || [0, 0, 0];
  const center = [(min[0] + max[0]) / 2, min[1], (min[2] + max[2]) / 2];
  const stamp = Date.now();

  const envId = `env-${stamp}`;
  const environment = {
    type: 'environment',
    url: job.result_glb_url,
    metadata: {
      id: envId,
      // SimpleEnvironmentModel recentres x/z and grounds min-y before applying
      // the transform; this offset puts the GLB's own origin back at 0.
      position: [r3(s * center[0]), r3(s * center[1]), r3(s * center[2])],
      scale: s,
      rotation: [0, 0, 0],
      visible: true,
      isLocked: true,
      collision: 'auto',
    },
  };

  const groupId = `seats-${stamp.toString(36)}`;
  const seats = (meta.seats || []).map((seat, i) => ({
    type: 'primitive',
    url: '',
    metadata: {
      id: `prim-${stamp}-${String(i).padStart(3, '0')}${rid().slice(0, 2)}`,
      shape: 'box',
      name: 'Studio Seat',
      position: [r3(s * seat.position[0]), r3(s * seat.position[1] - 0.25), r3(s * seat.position[2])],
      rotation: [0, r3(seat.yaw || 0), 0],
      scale: [0.5, 0.5, 0.5],
      color: '#888888',
      texturePreset: null,
      textureUrl: null,
      pbrMaps: null,
      textureScale: 2,
      visible: true,
      isLocked: true,
      collidable: false,
      colliderOnly: false,
      opacity: 0,
      metalness: 0,
      roughness: 0.5,
      emissive: 0,
      seat: true,
      groupId,
    },
  }));

  const spawn = meta.spawn || [0, 0, 0];
  const settings: Record<string, unknown> = {
    max_users: 20,
    // Private until the owner has walked it: AI output gets a look first.
    is_public: false,
    access_code: Math.random().toString(36).substring(2, 8).toUpperCase(),
    spawnPosition: [r3(s * spawn[0]), r3(s * spawn[1]), r3(s * spawn[2])],
    ...(job.options?.experience_type ? { experience_type: job.options.experience_type } : {}),
    // Same ground/atmosphere as the Conference Room: the GLB carries its own
    // floor and baked light, so the app's ground plane and sun stay out of it.
    ground: {
      size: 30, color: '#1a1c20', pbrMaps: null, visible: false, showGrid: false,
      textureUrl: null, textureScale: 10, texturePreset: null,
    },
    atmosphere: {
      fog: 'none', fogNear: 10, fogFar: 100, fogColor: '#101214', fogDensity: 0.02,
      bloom: true, bloomIntensity: 0.6, bloomThreshold: 0.8,
      particles: 'none', particleColor: '#ffffff', particleCount: 0,
      sunColor: '#fff1e0', sunIntensity: 0.7, sunShadows: false, ambientIntensity: 0.75,
      ...(sky ? SKY_LIGHT[sky.mood] : {}),
    },
    mobileSkyboxUrl: null,
    ...(job.concept_image_url ? { thumbnail_url: job.concept_image_url } : {}),
    studio_job_id: job.id,
  };

  const name = (meta.title || job.prompt).trim().slice(0, 60) || 'Studio World';
  const skyId = `skybox-${stamp}`;
  const skybox = sky ? [{
    type: 'skybox',
    url: sky.url,
    metadata: { id: skyId, filename: sky.filename, uploadedAt: stamp, isCurrent: true },
  }] : [];
  return { name, settings, assets: [environment, ...seats, ...skybox] };
}

/** Create a world from a finished `world` job; returns the new world id. */
export async function importAsWorld(
  job: StudioJob,
  onProgress?: (msg: string) => void,
): Promise<{ worldId: string; name: string; mock?: boolean }> {
  const m = mock();
  const mood = skyMoodFor(job);
  // Mock never calls Blockade: the preset stands in.
  const sky = m ? { url: PRESET_SKY[mood], mood, filename: `Studio ${mood} sky` } : await skyboxFor(job, onProgress);
  onProgress?.('Creating the world…');
  const plan = buildWorldImport(job, sky);
  if (m) {
    // Dry run: nothing is written in mock mode.
    console.info('[Studio mock] save_world payload (not sent):', plan);
    return { worldId: 'mock-world', name: plan.name, mock: true };
  }
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in first.');
  // save_world creates the worlds row and inserts every asset in one call,
  // stamping client_id from metadata.id (same path the builder's Save uses).
  const { data, error } = await supabase.rpc('save_world', {
    p_world_id: null,
    p_world_name: plan.name,
    p_owner_id: user.id,
    p_settings: plan.settings,
    p_assets: plan.assets,
  });
  if (error) throw new Error(error.message || 'Could not create the world.');
  return { worldId: data as string, name: plan.name };
}

// ---- Import: model ---------------------------------------------------------

export interface OwnedWorld {
  id: string;
  name: string;
  thumbnail_url: string | null;
  updated_at: string;
  settings: { spawnPosition?: [number, number, number]; is_public?: boolean } | null;
}

export async function listMyWorlds(): Promise<OwnedWorld[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('worlds')
    .select('id,name,thumbnail_url,updated_at,settings')
    .eq('owner_id', user.id)
    .order('updated_at', { ascending: false })
    .limit(60);
  if (error) throw error;
  return (data || []) as OwnedWorld[];
}

/** The world_assets row for a model, in the builder's `content` shape
 *  (worldPersistence.serializeRow). world_assets has no 'model' type: models
 *  are `type: 'content'` with `metadata.contentType: 'model'`. */
export function buildModelAsset(job: StudioJob, worldId: string, spawn: [number, number, number]) {
  if (!job.result_glb_url) throw new Error('This job has no GLB yet.');
  const meta = job.result_meta || {};
  const id = `content-${Date.now()}-${rid().slice(0, 4)}`;
  const minY = meta.bounds?.min?.[1] ?? 0;
  // At the spawn point, nudged 2 m forward (-Z) so nobody spawns inside it,
  // and lifted so the model's lowest point rests on the spawn's floor.
  const position: [number, number, number] = [r3(spawn[0]), r3(spawn[1] - minY), r3(spawn[2] - 2)];
  const filename = (meta.title || job.prompt).trim().slice(0, 40) || 'Studio model';
  return {
    world_id: worldId,
    client_id: id,
    type: 'content',
    url: job.result_glb_url,
    metadata: {
      id,
      position,
      scale: 1,
      rotation: [0, 0, 0],
      visible: true,
      filename,
      thumbnailUrl: job.preview_urls[job.preview_urls.length - 1] || job.concept_image_url || null,
      uploadedAt: Date.now(),
      contentType: 'model',
      collidable: false,
      seat: false,
      environmentMesh: false,
    },
  };
}

export async function addModelToWorld(job: StudioJob, worldId: string): Promise<{ mock?: boolean }> {
  const m = mock();
  let spawn: [number, number, number] = [0, 0, 0];
  if (!m) {
    const { data: world, error } = await supabase.from('worlds').select('settings').eq('id', worldId).single();
    if (error) throw new Error(error.message || 'Could not read that world.');
    const sp = (world?.settings as any)?.spawnPosition;
    if (Array.isArray(sp) && sp.length === 3) spawn = sp as [number, number, number];
  }
  const row = buildModelAsset(job, worldId, spawn);
  if (m) {
    console.info('[Studio mock] world_assets upsert (not sent):', row);
    return { mock: true };
  }
  const { error } = await supabase
    .from('world_assets')
    .upsert({ ...row, updated_at: new Date().toISOString() } as any, { onConflict: 'world_id,client_id' });
  if (error) throw new Error(error.message || 'Could not add the model.');
  return {};
}

// ---- Admin gate ------------------------------------------------------------

/** Studio credits a build costs — mirrors public.studio_units (model 1/2/3, world 2/3/6). */
export function studioCredits(kind: StudioKind, quality: string): number {
  const q = quality === 'draft' ? 0 : quality === 'hq' ? 2 : 1;
  return kind === 'model' ? [1, 2, 3][q] : [2, 3, 6][q];
}

export interface StudioCreditState {
  admin: boolean;
  plan: string | null;
  limit: number;
  used: number;
  remaining: number;
  packUnits: number;
}

/** Credits from a paid plan + Studio packs (public.studio_access). */
export async function getStudioCredits(): Promise<StudioCreditState | null> {
  if (mock()) return { admin: true, plan: null, limit: 0, used: 0, remaining: Infinity, packUnits: 6 };
  const { data, error } = await supabase.rpc('studio_access');
  if (error || !data?.signed_in) return null;
  return {
    admin: !!data.admin, plan: data.plan ?? null, limit: Number(data.limit ?? 0), used: Number(data.used ?? 0),
    remaining: data.admin ? Infinity : Number(data.remaining ?? 0), packUnits: Number(data.pack_units ?? 6),
  };
}

export async function checkStudioAccess(): Promise<'admin' | 'member' | 'signed-out'> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 'signed-out';
  const { data, error } = await supabase.rpc('is_app_admin');
  return !error && data === true ? 'admin' : 'member';
}

/** Dev mock only: the seeded job `?mock=<status>` asked to open, if any. */
export async function devInitialJobId(): Promise<string | null> {
  const m = mock();
  if (!m) return null;
  return (await m).mockFrozenJobId();
}
